import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../../components/custom_components/Modal'
import PerText from '../../components/custom_components/Text'
import RadioGroup from 'antd/lib/radio/group'
import { Radio } from 'antd'
import PerSpacer from '../../components/custom_components/Spacer'
import { useAuth } from '../../context/auth-context'
import TextArea from 'antd/lib/input/TextArea'
import { Country, RoleName } from '../../types'
import { managerForceMeetingConfirmation } from '../../graphql/meeting/hooks/useManagerForceMeetingConfirmation'
import { showNotification } from '../../components/custom_components/Notification/notification'

/** Where force-confirmation was triggered from — drives Apollo `refetchQueries` after mutation. */
export enum ForceConfirmationRefetchContext {
  /** Manager / subordinate meetings lists + related employee short info */
  MeetingsLists = 'MeetingsLists',
  /** Company Settings → Appraisal tab (`EMPLOYEES_AND_APPRAISALS`) */
  CompanyAppraisalSettings = 'CompanyAppraisalSettings',
  /** Company Manager dashboard monitoring (counters + table) */
  CompanyManagerDashboardMonitoring = 'CompanyManagerDashboardMonitoring',
}

type ForceConfirmationModalProps = {
  companyId: string
  country: Country
  meetingId: string
  employeeFirstName: string
  onClose: () => void
  refetchContext: ForceConfirmationRefetchContext
  userDashboardRoles?: RoleName[]
}

export const ForceConfirmationModal = (props: ForceConfirmationModalProps): JSX.Element => {
  const { companyId, country, meetingId, employeeFirstName, onClose, refetchContext, userDashboardRoles } = props

  const { t } = useTranslation()
  const { user } = useAuth()
  const [reason, setReason] = useState<string | undefined>(undefined)
  const [freeText, setFreeText] = useState<string>('')
  const [freeTextVisible, setFreeTextVisible] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isSubmittingCloseMeeting, toggleisSubmittingCloseMeeting] = useState<boolean>(false)

  const MAX_CHARS = 4000

  const forceConfirmation = async () => {
    if (isSubmittingCloseMeeting || !reason || (reason === t('forceConfirmationModal.other') && !freeText)) {
      return
    }
    toggleisSubmittingCloseMeeting(true)
    const reasonToSave = reason === t('forceConfirmationModal.other') ? freeText : reason
    const { errors } = await managerForceMeetingConfirmation(
      companyId,
      country,
      meetingId,
      reasonToSave,
      user.employeeId,
      refetchContext,
      userDashboardRoles,
    )
    if (errors && errors.length) {
      if (errors[0].extensions && errors[0].extensions.validationErrors.length > 0) {
        setErrorMessage(errors[0].extensions.validationErrors[0].message)
      }
      toggleisSubmittingCloseMeeting(false)
    } else {
      showNotification({
        type: 'success',
        message: t(`forceConfirmationModal.notifications.success.title`),
        description: t(`forceConfirmationModal.notifications.success.description`),
      })
      toggleisSubmittingCloseMeeting(false)
      onClose()
    }
  }

  const confirmButtonDisabled = isSubmittingCloseMeeting || !reason || (reason === t('forceConfirmationModal.other') && !freeText)

  return (
    <PerModal
      title={{
        heading: t('forceConfirmationModal.heading'),
      }}
      centered
      visible={!!meetingId}
      onCancel={() => {
        onClose()
        setErrorMessage('')
        setFreeText('')
        setFreeTextVisible(false)
      }}
      footer={{
        cancelBtn: {
          onClick: () => {
            onClose()
            setErrorMessage('')
            setFreeText('')
            setFreeTextVisible(false)
          },
        },
        confirmBtn: {
          onClick: async (e) => {
            e.preventDefault() // We should refactor button to not emit native event!
            await forceConfirmation()
          },
          disabled: confirmButtonDisabled,
          isProcessing: { active: isSubmittingCloseMeeting },
          tooltip: confirmButtonDisabled ? t('forceConfirmationModal.reasonTooltip') : '',
        },
      }}
    >
      <PerText fontWeight="bold">{t('forceConfirmationModal.description', { employeeFirstName })}</PerText>
      <PerText mb="s">{t('forceConfirmationModal.description2', { employeeFirstName })}</PerText>

      <PerText mb="xs" fontSize="s" fontWeight="bold">
        {t('forceConfirmationModal.selectReason')}
      </PerText>
      <RadioGroup
        name="hrResult"
        onChange={(event) => {
          setReason(event.target.value)
          setFreeTextVisible(event.target.value === t('forceConfirmationModal.other'))
        }}
        value={reason}
      >
        <Radio name="hrResult" value={t('forceConfirmationModal.parentalLeave')}>
          {t('forceConfirmationModal.parentalLeave')}
        </Radio>
        <PerSpacer mt={'xxs'} />
        <Radio name="hrResult" value={t('forceConfirmationModal.longtermSickness')}>
          {t('forceConfirmationModal.longtermSickness')}
        </Radio>
        <PerSpacer mt={'xxs'} />
        <Radio name="hrResult" value={t('forceConfirmationModal.sabbatical')}>
          {t('forceConfirmationModal.sabbatical')}
        </Radio>
        <PerSpacer mt={'xxs'} />
        <Radio name="hrResult" value={t('forceConfirmationModal.leaveOfAbsence')}>
          {t('forceConfirmationModal.leaveOfAbsence')}
        </Radio>
        <PerSpacer mt={'xxs'} />
        <Radio name="hrResult" value={t('forceConfirmationModal.other')}>
          {t('forceConfirmationModal.other')}
        </Radio>
      </RadioGroup>
      {freeTextVisible && (
        <>
          <PerSpacer mb="xs" />
          <TextArea
            maxLength={MAX_CHARS}
            rows={3}
            value={freeText}
            placeholder={t('forceConfirmationModal.placeholder')}
            onChange={(e) => {
              e.preventDefault()
              setFreeText(e.target.value)
            }}
          />
        </>
      )}
      {!!errorMessage && (
        <PerText color="error" fontSize="m" mt="m">
          {errorMessage}
        </PerText>
      )}
    </PerModal>
  )
}
